import { useEffect, useRef } from 'react'

export type WorkOffsetRegister = 'G54' | 'G55' | 'G56' | 'G57' | 'G58' | 'G59'
export const REGISTERS: readonly WorkOffsetRegister[] = ['G54', 'G55', 'G56', 'G57', 'G58', 'G59']

/**
 * The right-click origin popup for the Agent-flow part preview.
 *
 * Anchored at the cursor (container-relative px). Shows what the click snapped to, the live
 * coordinates, a register picker, and Confirm / Cancel. Confirm commits; Cancel / Esc /
 * click-outside discard. Purely presentational — the wrapper owns all state.
 */
export function OriginPickerPopup({
  anchor,
  snapLabel,
  position,
  register,
  onRegisterChange,
  onConfirm,
  onCancel,
}: {
  anchor: { x: number; y: number }
  snapLabel: string
  position: { x: number; y: number; z: number }
  register: WorkOffsetRegister
  onRegisterChange: (r: WorkOffsetRegister) => void
  onConfirm: () => void
  onCancel: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)

  // Esc to cancel; click outside to cancel.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onCancel()
    }
    window.addEventListener('keydown', onKey)
    // defer so the right-click that opened the popup doesn't immediately close it
    const t = window.setTimeout(() => window.addEventListener('mousedown', onDown), 0)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.clearTimeout(t)
      window.removeEventListener('mousedown', onDown)
    }
  }, [onCancel])

  // Keep the popup inside the container by flipping when near the right/bottom edge.
  const style: React.CSSProperties = {
    left: anchor.x,
    top: anchor.y,
    transform: `translate(${anchor.x > 0.6 * (ref.current?.parentElement?.clientWidth ?? 99999) ? '-100%' : '0'}, 0)`,
  }

  return (
    <div
      ref={ref}
      className="absolute z-30 w-[230px] rounded-xl border border-cyan-500/20 bg-[#030712]/95 backdrop-blur-sm px-3 py-3 shadow-2xl"
      style={style}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="mb-2 flex items-center gap-1.5">
        <span aria-hidden>📍</span>
        <span className="font-mono text-[11px] font-semibold text-amber-300">
          Snapped to {snapLabel}
        </span>
      </div>
      <div className="mb-3 font-mono text-[11px] text-slate-300">
        X{position.x.toFixed(2)}&nbsp;&nbsp;Y{position.y.toFixed(2)}&nbsp;&nbsp;Z{position.z.toFixed(2)}
      </div>

      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Register</span>
        <select
          value={register}
          onChange={(e) => onRegisterChange(e.target.value as WorkOffsetRegister)}
          className="rounded border border-cyan-500/20 bg-white/5 px-2 py-1 font-mono text-xs text-cyan-300 outline-none"
        >
          {REGISTERS.map((r) => (
            <option key={r} value={r} className="bg-[#0d1526]">{r}</option>
          ))}
        </select>
      </div>

      <div className="flex gap-2">
        <button
          onClick={onCancel}
          className="flex-1 rounded-lg border border-cyan-500/15 py-1.5 font-mono text-[11px] uppercase tracking-widest text-slate-400 transition-colors hover:text-slate-200"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="flex-1 rounded-lg bg-sky-500 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-widest text-white transition-colors hover:bg-sky-400"
        >
          Confirm
        </button>
      </div>
    </div>
  )
}
